import React, { useState, useEffect } from 'react';
import { Offcanvas, Button, Form, Table } from 'react-bootstrap';
import axios from 'axios';
import Swal from 'sweetalert2';
import Select from 'react-select';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';

const UploadedQuotationProductsOffcanvas = ({ show, handleClose, deal, onSuccess }) => {
    const [brands, setBrands] = useState([]);
    const [selectedBrands, setSelectedBrands] = useState([]);
    const [products, setProducts] = useState([]);
    const [filteredProducts, setFilteredProducts] = useState([]);
    const [modelType, setModelType] = useState('');
    const [currentStep, setCurrentStep] = useState(1);

    // Additional tracking for per-product details
    // For each product we need: quantity, price, deployment_description, deployment_amount
    const [productDetails, setProductDetails] = useState({});

    const [customIntegrationDescription, setCustomIntegrationDescription] = useState("");
    const [customIntegrationTotal, setCustomIntegrationTotal] = useState(0);
    const [overallDeploymentAmount, setOverallDeploymentAmount] = useState(0);

    // Track which product IDs were loaded from saved quotation data
    // so the filteredProducts effect doesn't overwrite them with original prices
    const savedFromQuotation = React.useRef(new Set());

    // Fresh quotation data fetched directly from the API on each open
    const [liveQuotation, setLiveQuotation] = useState(null);

    const backendUrl = process.env.REACT_APP_BACKEND_URL;
    const userInfo = JSON.parse(localStorage.getItem("user-info"));
    const token = userInfo?.token;
    const origin = localStorage.getItem('user-origin');

    // Fetch fresh quotation data from the API
    const fetchQuotation = async (quotationId) => {
        try {
            const response = await axios.get(`${backendUrl}/api/quotations/${quotationId}`, {
                headers: { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin },
                skipLoader: true,
            });
            setLiveQuotation(response.data.quotation || null);
        } catch (error) {
            console.error('Error fetching quotation:', error);
            // Fall back to deal.quotation if API call fails
            setLiveQuotation(deal?.quotation || null);
        }
    };

    // When the offcanvas opens, reset state and fetch fresh data
    useEffect(() => {
        if (show && deal) {
            // Reset form state on every open
            setCurrentStep(1);
            setSelectedBrands([]);
            setProductDetails({});
            setFilteredProducts([]);
            setModelType('');
            setCustomIntegrationDescription('');
            setCustomIntegrationTotal(0);
            setLiveQuotation(null);
            savedFromQuotation.current = new Set(); // reset saved set on open

            // Fetch brands, products, and latest quotation in parallel
            fetchBrands();
            fetchProducts();
            if (deal.quotation_id) {
                fetchQuotation(deal.quotation_id);
            }
        }
    }, [show, deal]);

    // Initialize form once we have fresh quotation data + brands + products loaded
    useEffect(() => {
        // Use liveQuotation (fresh API data) — fall back to deal.quotation if still loading
        const quot = liveQuotation || deal?.quotation;
        if (!show || !quot || brands.length === 0 || products.length === 0) return;

        // Set Model Type
        if (quot.model_type) setModelType(quot.model_type);

        // Set Custom Integration
        setCustomIntegrationDescription(quot.custom_integration_description || "");
        setCustomIntegrationTotal(quot.custom_integration_total || 0);

        // Calculate and extract the overall explicit deployment amount
        // since QuotationForm saves the mathematical SUM in deplyment_amount
        // we might not know exactly what the original base was, but we can fallback to 0 or extract from quot.
        // Wait, if quotient has deplyment_amount but it includes products + custom... we shouldn't over-read it. 
        // Let's just trust quot.overall_deployment_amount if it exists, otherwise leave it empty so users enter it.
        // But for exact parity we just init from 0 if it's missing.
        setOverallDeploymentAmount(quot.editable_deployment_amount || 0);

        // Set Brands from brand_id stored on the quotation
        if (quot.brand_id) {
            const extractBrandIds = (bid) => {
                if (!bid) return [];
                if (Array.isArray(bid)) return bid.map(id => parseInt(id));
                if (typeof bid === 'string') {
                    try {
                        const parsed = JSON.parse(bid);
                        return Array.isArray(parsed)
                            ? (Array.isArray(parsed[0]) ? parsed.flat().map(id => parseInt(id)) : parsed.map(id => parseInt(id)))
                            : [parseInt(parsed)];
                    } catch (e) {
                        return bid.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
                    }
                }
                return [parseInt(bid)];
            };

            const bIds = extractBrandIds(quot.brand_id);
            console.log("Extracted Brand IDs from quotation:", bIds);

            const selected = brands
                .filter(b => bIds.includes(parseInt(b.id)))
                .map(b => ({ value: b.id, label: b.name }));

            if (selected.length > 0) {
                setSelectedBrands(selected);
            }
        }

        // Set Product Details from quotation's product_item_details
        const itemDetails = quot.product_item_details || quot.productItemDetails;
        if (Array.isArray(itemDetails) && itemDetails.length > 0) {
            const details = {};

            // JSON keys are always strings — parse carefully
            const editableDeploymentDescs = typeof quot.editable_deployment_descriptions === 'string'
                ? JSON.parse(quot.editable_deployment_descriptions)
                : (quot.editable_deployment_descriptions || {});

            const editableDeploymentAmts = typeof quot.editable_deployment_amounts === 'string'
                ? JSON.parse(quot.editable_deployment_amounts)
                : (quot.editable_deployment_amounts || {});

            itemDetails.forEach(item => {
                const pId = item.product_id;
                const pIdStr = String(pId); // JSON keys are always strings
                const productObj = products.find(p => p.id === pId);

                // Resolve deployment_amount: editable saved value > product pricings > product field > 0
                const savedDeploymentAmt = editableDeploymentAmts[pIdStr] ?? editableDeploymentAmts[pId] ?? null;
                let resolvedDeploymentAmount = 0;
                if (savedDeploymentAmt !== null && savedDeploymentAmt !== undefined) {
                    resolvedDeploymentAmount = parseFloat(savedDeploymentAmt);
                } else {
                    // Fall back to deployment_cost from the product's pricings array
                    // item.product comes from the quotation API (productItemDetails.product.pricings)
                    const itemProductPricings = item.product?.pricings || [];
                    const productObjPricings = productObj?.pricings || [];
                    const rawDeploymentCost = parseFloat(
                        itemProductPricings[0]?.deployment_cost ||
                        productObjPricings[0]?.deployment_cost ||
                        item.product?.deployment_cost ||
                        productObj?.deployment_cost ||
                        0
                    );
                    const productCurrency = getProductCurrency(productObj || item.product);
                    resolvedDeploymentAmount = convertPrice(rawDeploymentCost, productCurrency);
                }

                details[pId] = {
                    quantity: item.quantity,
                    price: item.price,
                    item_description: item.item_description || '',
                    deployment_description:
                        editableDeploymentDescs[pIdStr] ||
                        editableDeploymentDescs[pId] ||
                        productObj?.deployment_description ||
                        `${item.item_name} Deployment & Configuration`,
                    deployment_amount: parseFloat(resolvedDeploymentAmount) || 0,
                    min_price: productObj
                        ? convertPrice(productObj.pricings?.[0]?.min_price || productObj.min_price || 0, getProductCurrency(productObj))
                        : 0
                };
            });
            setProductDetails(details);
            // Mark all loaded product IDs as coming from saved quotation data
            Object.keys(details).forEach(id => savedFromQuotation.current.add(String(id)));
        }
    }, [show, liveQuotation, deal, brands, products]);

    const fetchBrands = async () => {
        try {
            const response = await axios.get(`${backendUrl}/api/brands`, {
                headers: { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin },
                skipLoader: true,
            });
            setBrands(response.data.brand || []);
        } catch (error) {
            console.error('Error fetching brands:', error);
        }
    };

    const fetchProducts = async () => {
        try {
            const response = await axios.get(`${backendUrl}/api/products`, {
                headers: { Authorization: `Bearer ${token}`, 'Tenant-Origin': origin },
                skipLoader: true,
            });
            let productsData = [];
            if (Array.isArray(response.data.Data)) {
                productsData = response.data.Data;
            } else if (response.data.Data && Array.isArray(response.data.Data.data)) {
                productsData = response.data.Data.data;
            } else if (response.data.data && Array.isArray(response.data.data)) {
                productsData = response.data.data;
            }
            setProducts(productsData);
        } catch (error) {
            console.error('Error fetching products:', error);
        }
    };

    useEffect(() => {
        if (selectedBrands.length > 0) {
            const brandIds = selectedBrands.map(b => b.value);
            const filtered = products.filter(p => brandIds.includes(p.brand_id));
            setFilteredProducts(filtered.map(p => ({
                value: p.id,
                label: p.product_name,
                product: p
            })));
        } else {
            setFilteredProducts([]);
        }
    }, [selectedBrands, products]);

    const usdToAedExchangeRate = 3.67;
    const currentCurrency = deal?.quotation?.project?.client?.currency || deal?.organisation?.currency || 'AED';

    const getProductCurrency = (product) => {
        if (!product) return 'AED';
        return product.product_currency || product.currency || product.vendor?.currency || 'AED';
    };

    const convertPrice = (price, productCurrency = 'AED') => {
        if (price === undefined || price === null) return 0;
        const priceNumber = parseFloat(price);
        if (isNaN(priceNumber)) return 0;

        // Get dynamic exchange rate from localStorage
        const storedRate = localStorage.getItem('exchange_rate_usd_aed');
        const rate = storedRate ? parseFloat(storedRate) : 3.67;

        let converted = priceNumber;
        if (productCurrency !== currentCurrency) {
            if (productCurrency === 'AED' && currentCurrency === 'USD') {
                converted = priceNumber / rate;
            } else if (productCurrency === 'USD' && currentCurrency === 'AED') {
                converted = priceNumber * rate;
            }
        }
        return parseFloat(converted.toFixed(2));
    };

    useEffect(() => {
        setProductDetails(prev => {
            const newDetails = { ...prev };
            let hasChanges = false;

            filteredProducts.forEach(fp => {
                const pId = fp.value;

                // Skip products already loaded from saved quotation data.
                // This prevents overwriting the saved price/deployment with the product catalog default.
                if (savedFromQuotation.current.has(String(pId))) return;

                if (!newDetails[pId]) {
                    hasChanges = true;
                    const opt = fp;
                    const pricings = opt.product.pricings || [];
                    const firstPricing = pricings[0] || {};
                    const productCurrency = getProductCurrency(opt.product);

                    const rawPrice = parseFloat(firstPricing.price || opt.product.selling_price || 0);
                    const rawDeploymentCost = parseFloat(firstPricing.deployment_cost || opt.product.deployment_cost || 0);
                    const rawMinPrice = parseFloat(firstPricing.min_price || opt.product.min_price || 0);

                    const price = convertPrice(rawPrice, productCurrency);
                    const deploymentCost = convertPrice(rawDeploymentCost, productCurrency);
                    const minPrice = convertPrice(rawMinPrice, productCurrency);

                    const deploymentDesc = opt.product.deployment_description || `${opt.product.item_name || opt.product.product_name} Deployment & Configuration`;

                    const productNameLower = (opt.product.item_name || opt.product.product_name || "").toLowerCase();
                    let initialQty = 0;
                    if (modelType === 'new') {
                        if (productNameLower.includes('administrator') || productNameLower.includes('supervisor')) {
                            initialQty = 1;
                        }
                    }

                    newDetails[pId] = {
                        quantity: initialQty,
                        price: price,
                        item_description: opt.product.description || '',
                        deployment_description: deploymentDesc,
                        deployment_amount: deploymentCost,
                        min_price: minPrice
                    };
                }
            });
            return hasChanges ? newDetails : prev;
        });
    }, [filteredProducts, modelType, currentCurrency]);
    // NOTE: savedFromQuotation.current is a Set of product IDs loaded from saved data.
    // Products in this set are skipped here so their saved prices are preserved.


    const handleDetailChange = (pId, field, value) => {
        // Mark this product as user-touched so the filteredProducts effect won't overwrite it
        savedFromQuotation.current.add(String(pId));
        setProductDetails(prev => ({
            ...prev,
            [pId]: {
                ...prev[pId],
                [field]: value
            }
        }));
    };

    const handleDeploymentBlur = (pId) => {
        setProductDetails(prev => {
            const productDetail = prev[pId];
            let newAmount = parseFloat(productDetail.deployment_amount);
            if (isNaN(newAmount)) newAmount = 0;

            return {
                ...prev,
                [pId]: {
                    ...productDetail,
                    deployment_amount: parseFloat(newAmount.toFixed(2))
                }
            };
        });
    };

    const handleCustomIntegrationBlur = () => {
        let value = parseFloat(customIntegrationTotal);
        if (isNaN(value)) value = 0;
        setCustomIntegrationTotal(parseFloat(value.toFixed(2)));
    };

    const handlePriceBlur = (pId) => {
        setProductDetails(prev => {
            const productDetail = prev[pId];
            let newPrice = parseFloat(productDetail.price);
            if (isNaN(newPrice)) newPrice = 0;

            const minPrice = parseFloat(productDetail.min_price) || 0;

            if (newPrice < minPrice) {
                newPrice = minPrice;
                Swal.fire({
                    icon: 'warning',
                    text: `The amount cannot be less than the minimum price of ${minPrice.toFixed(2)} ${currentCurrency}.`,
                    toast: true,
                    position: 'top-end',
                    showConfirmButton: false,
                    timer: 3000
                });
            }

            return {
                ...prev,
                [pId]: {
                    ...productDetail,
                    price: parseFloat(newPrice.toFixed(2))
                }
            };
        });
    };

    const handleSubmit = async () => {
        const chosenProducts = filteredProducts.filter(opt => {
            const details = productDetails[opt.value];
            return details && parseFloat(details.quantity) > 0;
        });

        if (chosenProducts.length === 0) {
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Please set a quantity greater than 0 for at least one product.',
            });
            return;
        }

        let hasError = false;
        // for (const opt of chosenProducts) {
        //     const details = productDetails[opt.value];
        //     const price = parseFloat(details.price) || 0;
        //     const minPrice = parseFloat(details.min_price) || 0;
        //     if (price < minPrice) {
        //         Swal.fire({
        //             icon: 'warning',
        //             text: `The amount for ${opt.label} cannot be less than ${minPrice}.`,
        //             toast: true,
        //             position: 'top-end',
        //             showConfirmButton: false,
        //             timer: 3000
        //         });
        //         hasError = true;
        //         break;
        //     }
        // }

        if (hasError) return;

        try {
            // Calculate totals
            let totalAmount = 0;
            let totalDeploymentAmount = 0;
            const formattedProducts = chosenProducts.map(opt => {
                const details = productDetails[opt.value];
                const subtotal = parseFloat(((parseFloat(details.quantity) || 0) * (parseFloat(details.price) || 0)).toFixed(2));
                totalAmount += subtotal;
                totalDeploymentAmount += parseFloat(details.deployment_amount || 0);

                return {
                    product_id: parseInt(opt.value),
                    item_name: opt.product.item_name || opt.product.product_name,
                    item_description: details.item_description || '',
                    price: parseFloat(details.price),
                    quantity: parseInt(details.quantity),
                    subtotal: subtotal
                };
            });

            totalDeploymentAmount = parseFloat(totalDeploymentAmount.toFixed(2));
            const integrationAmount = parseFloat(parseFloat(customIntegrationTotal || 0).toFixed(2));
            const fixedDeployment = parseFloat(overallDeploymentAmount || 0);
            totalAmount = parseFloat((totalAmount + totalDeploymentAmount + integrationAmount + fixedDeployment).toFixed(2));

            const editableDeploymentDescriptions = {};
            const editableDeploymentAmounts = {};
            chosenProducts.forEach(opt => {
                const details = productDetails[opt.value];
                editableDeploymentDescriptions[opt.value] = details.deployment_description;
                editableDeploymentAmounts[opt.value] = details.deployment_amount;
            });

            const payload = {
                // amount: totalAmount,
                // total_amount_with_subscription: parseFloat((totalAmount - totalDeploymentAmount - integrationAmount).toFixed(2)),
                // deplyment_amount: totalDeploymentAmount,
                model_type: modelType,
                custom_integration_description: customIntegrationDescription,
                custom_integration_total: integrationAmount,
                deplyment_amount: parseFloat((totalDeploymentAmount + integrationAmount + fixedDeployment).toFixed(2)),
                editable_deployment_amount: fixedDeployment, // Optional field tracking
                skip_versioning: true,
                editable_deployment_descriptions: JSON.stringify(editableDeploymentDescriptions),
                editable_deployment_amounts: JSON.stringify(editableDeploymentAmounts),
                products: formattedProducts,
                brand_id: selectedBrands.map(b => b.value)
            };

            await axios.put(`${backendUrl}/api/quotations/${deal.quotation_id}`, payload, {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Tenant-Origin': origin,
                }
            });

            // Removed deal value sync to preserve manual totals entered in upload form

            Swal.fire({
                icon: 'success',
                title: 'Success',
                text: 'Products and Deal value updated successfully',
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 3000
            });
            onSuccess();
            handleClose();
        } catch (error) {
            console.error('Error updating quotation products:', error);
            Swal.fire({
                icon: 'error',
                title: 'Error',
                text: 'Failed to add products'
            });
        }
    };

    const brandOptions = brands.map(b => ({ value: b.id, label: b.name }));

    const chosenProductsForDeployment = filteredProducts.filter(opt => {
        const details = productDetails[opt.value];
        return details && parseFloat(details.quantity) > 0;
    });

    const StepProgress = () => (
        <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center position-relative">
                {[1, 2, 3].map((step) => (
                    <div key={step} className="d-flex flex-column align-items-center">
                        <div
                            className={`rounded-circle d-flex align-items-center justify-content-center ${currentStep >= step
                                ? 'bg-primary text-white'
                                : 'bg-light border text-muted'
                                }`}
                            style={{
                                width: '30px',
                                height: '30px',
                                fontSize: '12px',
                                fontWeight: '600',
                                zIndex: 2
                            }}
                        >
                            {step}
                        </div>
                        <div
                            className="mt-2 text-center"
                            style={{
                                fontSize: '12px',
                                fontWeight: currentStep === step ? '600' : '400',
                                color: currentStep >= step ? '#000' : '#6c757d'
                            }}
                        >
                            {step === 1 && 'Configuration'}
                            {step === 2 && 'Products'}
                            {step === 3 && 'Deployment'}
                        </div>
                    </div>
                ))}
                <div
                    className="position-absolute top-50 start-0 end-0"
                    style={{
                        height: '2px',
                        backgroundColor: '#e9ecef',
                        transform: 'translateY(-50%)',
                        zIndex: 1
                    }}
                ></div>
                <div
                    className="position-absolute top-50 start-0"
                    style={{
                        height: '2px',
                        backgroundColor: '#0d6efd',
                        transform: 'translateY(-50%)',
                        width: `${((currentStep - 1) / 2) * 100}%`,
                        zIndex: 1,
                        transition: 'width 0.3s ease'
                    }}
                ></div>
            </div>
        </div>
    );

    return (
        <Offcanvas show={show} onHide={handleClose} placement="end" style={{ width: '1050px' }}>
            <Offcanvas.Header closeButton>
                <Offcanvas.Title>Add Products to Uploaded Quotation</Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body style={{ overflowX: 'hidden' }}>
                <StepProgress />

                {currentStep === 1 && (
                    <div>
                        <div className="row mb-3">
                            <div className="col-md-12">
                                <Form.Group>
                                    <Form.Label>Model Type</Form.Label>
                                    <Form.Select
                                        value={modelType}
                                        onChange={(e) => setModelType(e.target.value)}
                                    >
                                        <option value="">Select Model</option>
                                        <option value="new">New</option>
                                        <option value="expand">Expand</option>
                                    </Form.Select>
                                </Form.Group>
                            </div>
                        </div>

                        <Form.Group className="mb-3">
                            <Form.Label>Select Brand(s)</Form.Label>
                            <Select
                                isMulti
                                options={brandOptions}
                                value={selectedBrands}
                                onChange={setSelectedBrands}
                                placeholder="Select brands..."
                            />
                        </Form.Group>

                        <div className="d-flex justify-content-end mt-4 pt-3 border-top">
                            <Button
                                variant="primary"
                                onClick={() => setCurrentStep(2)}
                                disabled={selectedBrands.length === 0 || !modelType}
                            >
                                Next: Products Configuration
                            </Button>
                        </div>
                    </div>
                )}

                {currentStep === 2 && (
                    <div>
                        {filteredProducts.length > 0 ? (
                            <div className="mt-3" style={{ overflow: 'hidden' }}>
                                <h6 className="mb-2 fw-semibold text-primary">Products Configuration</h6>
                                <table className="table table-bordered align-middle mb-0 shadow-sm w-100">
                                    <thead className="table-sales-gradient">
                                        <tr>
                                            <th style={{ width: "25%", fontSize: "13px" }} className="fw-medium py-2">Item</th>
                                            <th style={{ width: "30%", fontSize: "13px" }} className="fw-medium py-2">Description</th>
                                            <th style={{ width: "10%", fontSize: "13px" }} className="fw-medium py-2 text-center">Qty</th>
                                            <th style={{ width: "15%", fontSize: "13px" }} className="fw-medium py-2 text-center">Amount ({currentCurrency})</th>
                                            <th style={{ width: "20%", fontSize: "13px" }} className="fw-medium py-2 text-end">Subtotal ({currentCurrency})</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredProducts.map(opt => {
                                            const details = productDetails[opt.value] || {};
                                            const subtotal = (details.quantity || 0) * (details.price || 0);
                                            return (
                                                <tr key={`prod-${opt.value}`}>
                                                    <td style={{ fontSize: '12px' }}>{opt.label}</td>
                                                    <td>
                                                        <Form.Control
                                                            as="textarea"
                                                            rows={1}
                                                            size="sm"
                                                            value={details.item_description || ''}
                                                            onChange={(e) => handleDetailChange(opt.value, 'item_description', e.target.value)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <Form.Control
                                                            type="number"
                                                            size="sm"
                                                            value={details.quantity || 0}
                                                            onChange={(e) => handleDetailChange(opt.value, 'quantity', e.target.value)}
                                                            min="0"
                                                            className="text-center quantity-input"
                                                            style={{ fontSize: '13px' }}
                                                        />
                                                    </td>
                                                    <td className="text-center">
                                                        <Form.Control
                                                            type="number"
                                                            size="sm"
                                                            value={details.price || 0}
                                                            min={details.min_price || 0}
                                                            onChange={(e) => handleDetailChange(opt.value, 'price', e.target.value)}
                                                            onBlur={() => handlePriceBlur(opt.value)}
                                                            className="text-center"
                                                            style={{ fontSize: '13px' }}
                                                        />
                                                    </td>
                                                    <td className="text-end fw-medium" style={{ fontSize: '13px' }}>
                                                        {subtotal.toFixed(2)}
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="alert alert-warning text-center">No products found for the selected brand(s).</div>
                        )}

                        <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                            <Button variant="secondary" onClick={() => setCurrentStep(1)}>Back</Button>
                            <Button variant="primary" onClick={() => setCurrentStep(3)}>Next: Deployment Setup</Button>
                        </div>
                    </div>
                )}

                {currentStep === 3 && (
                    <div>
                        {chosenProductsForDeployment.length > 0 ? (
                            <div className="mt-1" style={{ overflow: 'hidden' }}>
                                <h6 className="mb-3 text-dark fw-semibold">Deployment Setup</h6>

                                {/* 1. ONE-TIME DEPLOYMENT CONFIG TABLE */}
                                <div className="mb-4 border rounded">
                                    <div className="py-2 px-3 text-white fw-semibold" style={{ backgroundColor: "#0f5194ff", fontSize: "14px" }}>
                                        One-Time Deployment Configuration & Testing
                                    </div>
                                    <div className="p-3">
                                        <div className="table-responsive">
                                            <table className="table table-bordered table-sm mb-0">
                                                <thead className="table-sales-gradient">
                                                    <tr>
                                                        <th style={{ fontSize: "12px", fontWeight: "bold" }}>Description</th>
                                                        <th style={{ fontSize: "12px", fontWeight: "bold" }} className="text-end">Amount ({currentCurrency})</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    <tr>
                                                        <td style={{ fontSize: "12px" }}>
                                                            One Time deployment, Configuration & Testing
                                                        </td>
                                                        <td style={{ fontSize: "12px" }} className="text-end">
                                                            <Form.Control
                                                                type="number"
                                                                size="sm"
                                                                value={overallDeploymentAmount}
                                                                onChange={(e) => setOverallDeploymentAmount(e.target.value)}
                                                                className="text-end ms-auto"
                                                                style={{ fontSize: '12px', maxWidth: '120px' }}
                                                            />
                                                        </td>
                                                    </tr>
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                </div>

                                {/* 2. PRODUCT SPECIFIC TABLE */}
                                {chosenProductsForDeployment.some(opt => (parseFloat(productDetails[opt.value]?.deployment_amount) || 0) > 0) && (
                                    <div className="mb-4 border rounded">
                                        <div className="py-2 px-3 text-white fw-semibold" style={{ backgroundColor: "#0f5194ff", fontSize: "14px" }}>
                                            Product-Specific Deployment Costs
                                        </div>
                                        <div className="p-3">
                                            <div className="table-responsive">
                                                <table className="table table-bordered table-sm mb-0">
                                                    <thead className="table-sales-gradient">
                                                        <tr>
                                                            <th style={{ width: "25%", fontSize: "12px", fontWeight: "bold" }}>Item</th>
                                                            <th style={{ width: "45%", fontSize: "12px", fontWeight: "bold" }}>Deployment Description</th>
                                                            <th style={{ width: "30%", fontSize: "12px", fontWeight: "bold" }} className="text-end">Amount ({currentCurrency})</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {chosenProductsForDeployment.map(opt => {
                                                            const details = productDetails[opt.value] || {};
                                                            if ((parseFloat(details.deployment_amount) || 0) <= 0) return null; // Don't show if 0

                                                            return (
                                                                <tr key={`dep-${opt.value}`}>
                                                                    <td style={{ fontSize: '12px', width: '25%' }}>{opt.label}</td>
                                                                    <td>
                                                                        <Form.Control
                                                                            as="textarea"
                                                                            rows={1}
                                                                            size="sm"
                                                                            value={details.deployment_description || ''}
                                                                            onChange={(e) => handleDetailChange(opt.value, 'deployment_description', e.target.value)}
                                                                            style={{ fontSize: "12px" }}
                                                                        />
                                                                    </td>
                                                                    <td>
                                                                        <Form.Control
                                                                            type="number"
                                                                            size="sm"
                                                                            value={details.deployment_amount || 0}
                                                                            onChange={(e) => handleDetailChange(opt.value, 'deployment_amount', e.target.value)}
                                                                            onBlur={() => handleDeploymentBlur(opt.value)}
                                                                            className="text-end ms-auto"
                                                                            style={{ fontSize: '12px', maxWidth: '120px' }}
                                                                        />
                                                                    </td>
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* 3. CUSTOM INTEGRATION TABLE */}
                                <div className="mb-3 border rounded">
                                    <div className="py-2 px-3 text-white fw-semibold" style={{ backgroundColor: "#1e4b7a", fontSize: "14px" }}>
                                        Custom Integration
                                    </div>
                                    <div className="p-3">
                                        <Form.Group className="mb-3">
                                            <Form.Label className="fw-medium" style={{ fontSize: "13px" }}>
                                                Integration Description
                                            </Form.Label>
                                            <ReactQuill
                                                theme="snow"
                                                value={customIntegrationDescription || ""}
                                                onChange={setCustomIntegrationDescription}
                                                style={{ height: "120px", marginBottom: "50px", fontSize: "13px" }}
                                            />
                                        </Form.Group>
                                        <Form.Group>
                                            <Form.Label className="fw-medium" style={{ fontSize: "13px" }}>
                                                Integration Cost
                                            </Form.Label>
                                            <div className="d-flex align-items-center gap-2">
                                                <Form.Control
                                                    type="number"
                                                    placeholder="Enter additional integration cost"
                                                    value={customIntegrationTotal || ""}
                                                    onChange={(e) => setCustomIntegrationTotal(parseFloat(e.target.value) || 0)}
                                                    onBlur={handleCustomIntegrationBlur}
                                                    style={{ fontSize: "13px", height: "38px", maxWidth: "200px" }}
                                                />
                                                <span className="text-muted" style={{ fontSize: "13px" }}>{currentCurrency}</span>
                                            </div>
                                        </Form.Group>
                                    </div>
                                </div>
                            </div>
                        ) : (
                            <div className="alert alert-info text-center">No products selected with quantity {'>'} 0.</div>
                        )}

                        {chosenProductsForDeployment.length > 0 ? (
                            <div className="mt-4 border-top pt-3">
                                {(() => {
                                    let subscriptionAmt = 0;
                                    let deploymentAmt = 0;
                                    chosenProductsForDeployment.forEach(opt => {
                                        const details = productDetails[opt.value] || {};
                                        subscriptionAmt += (parseFloat(details.quantity) || 0) * (parseFloat(details.price) || 0);
                                        deploymentAmt += parseFloat(details.deployment_amount) || 0;
                                    });
                                    const integrationAmt = parseFloat(customIntegrationTotal) || 0;
                                    const fixedDeploymentAmt = parseFloat(overallDeploymentAmount) || 0;
                                    const totalDeployment = deploymentAmt + fixedDeploymentAmt;
                                    const grandTotal = subscriptionAmt + totalDeployment + integrationAmt;

                                    return (
                                        <div style={{ border: "2px solid #28a745", backgroundColor: "#f8f9fa", borderRadius: "8px", padding: "16px" }}>
                                            <h6 className="fw-bold mb-3" style={{ fontSize: "14px", color: "#28a745" }}>One-Time Cost Summary</h6>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted fw-semibold" style={{ fontSize: "13px" }}>Product Subscription Total:</span>
                                                <span className="fw-bold" style={{ fontSize: "13px", color: "#333" }}>
                                                    {currentCurrency} {subscriptionAmt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted fw-semibold" style={{ fontSize: "13px" }}>Total Deployment (Fixed + Products):</span>
                                                <span className="fw-bold" style={{ fontSize: "13px", color: "#f39c12" }}>
                                                    {currentCurrency} {totalDeployment.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                            <div className="d-flex justify-content-between mb-2">
                                                <span className="text-muted fw-semibold" style={{ fontSize: "13px" }}>Custom Integration:</span>
                                                <span className="fw-bold" style={{ fontSize: "13px", color: "#9b59b6" }}>
                                                    {currentCurrency} {integrationAmt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                            <hr style={{ borderTop: "1px dashed #ccc", margin: "10px 0" }} />
                                            <div className="d-flex justify-content-between align-items-center">
                                                <span className="text-dark fw-bold" style={{ fontSize: "15px" }}>Grand Total:</span>
                                                <span className="fw-bold" style={{ fontSize: "16px", color: "#28a745" }}>
                                                    {currentCurrency} {grandTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                </span>
                                            </div>
                                        </div>
                                    );
                                })()}
                            </div>
                        ) : null}

                        <div className="d-flex justify-content-between mt-4 pt-3 border-top">
                            <Button variant="secondary" onClick={() => setCurrentStep(2)}>Back</Button>
                            <Button variant="success" onClick={handleSubmit}>Save & Confirm</Button>
                        </div>
                    </div>
                )}
            </Offcanvas.Body>
        </Offcanvas>
    );
};

export default UploadedQuotationProductsOffcanvas;
